import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, Combobox, Transition } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  MagnifyingGlassIcon,
  HomeIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
  AcademicCapIcon,
  ChartBarIcon,
  PlusIcon,
  ArrowRightIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  TruckIcon,
  ArchiveBoxIcon,
  BuildingOfficeIcon,
  FunnelIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';

interface CommandItem {
  id: string;
  name: string;
  description?: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  action?: () => void;
  category: 'navigation' | 'action' | 'recent';
  shortcut?: string;
}

interface CommandPaletteProps {
  isAdmin?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  showTrigger?: boolean;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isAdmin = false,
  isOpen: controlledIsOpen,
  onClose,
  showTrigger = true
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = (value: boolean) => {
    if (controlledIsOpen !== undefined) {
      if (!value && onClose) onClose();
    } else {
      setInternalIsOpen(value);
    }
  };

  // Navigation items based on user role
  const navigationItems: CommandItem[] = useMemo(() => {
    const baseItems: CommandItem[] = [
      { id: 'dashboard', name: 'Dashboard', icon: HomeIcon, href: '/dashboard', category: 'navigation' },
      { id: 'clients', name: 'Clients', icon: UsersIcon, href: '/clients', category: 'navigation' },
      { id: 'deals', name: 'Deals', icon: CurrencyDollarIcon, href: '/deals', category: 'navigation' },
      { id: 'tasks', name: 'Tasks', icon: ClipboardDocumentListIcon, href: '/tasks', category: 'navigation' },
      { id: 'catalogue', name: 'Catalogue', icon: BookOpenIcon, href: '/catalogue', category: 'navigation' },
      { id: 'training', name: 'Training', icon: AcademicCapIcon, href: '/training', category: 'navigation' },
      { id: 'email-templates', name: 'Email Templates', icon: DocumentTextIcon, href: '/clients/templates', category: 'navigation' },
      { id: 'email-history', name: 'Email History', icon: EnvelopeIcon, href: '/clients/emails', category: 'navigation' },
    ];

    const adminItems: CommandItem[] = [
      { id: 'admin-dashboard', name: 'Admin Dashboard', icon: HomeIcon, href: '/admin', category: 'navigation' },
      { id: 'analytics', name: 'Analytics', icon: ChartBarIcon, href: '/admin/analytics', category: 'navigation' },
      { id: 'team-management', name: 'Team Management', icon: UsersIcon, href: '/admin/users', category: 'navigation' },
      { id: 'admin-quotes', name: 'Quotes', icon: DocumentDuplicateIcon, href: '/admin/quotes', category: 'navigation' },
      { id: 'suppliers', name: 'Suppliers', icon: TruckIcon, href: '/admin/suppliers', category: 'navigation' },
      { id: 'inventory', name: 'Inventory', icon: ArchiveBoxIcon, href: '/admin/inventory', category: 'navigation' },
      { id: 'companies', name: 'Companies', icon: BuildingOfficeIcon, href: '/admin/companies', category: 'navigation' },
      { id: 'pipelines', name: 'Pipelines', icon: FunnelIcon, href: '/admin/pipelines', category: 'navigation' },
      { id: 'segments', name: 'Segments', icon: FunnelIcon, href: '/admin/segments', category: 'navigation' },
    ];

    return isAdmin ? [...adminItems, ...baseItems] : baseItems;
  }, [isAdmin]);

  // Quick actions
  const actionItems: CommandItem[] = useMemo(() => [
    { id: 'new-client', name: 'Create New Client', description: 'Add a new client to the system', icon: PlusIcon, href: '/clients/new', category: 'action', shortcut: '⌘N' },
    { id: 'new-deal', name: 'Create New Deal', description: 'Start a new deal', icon: PlusIcon, href: '/deals/new', category: 'action' },
    { id: 'new-task', name: 'Create New Task', description: 'Add a new task', icon: PlusIcon, href: '/tasks?new=true', category: 'action' },
  ], []);

  const allItems = useMemo(() => [...navigationItems, ...actionItems], [navigationItems, actionItems]);

  const filteredItems = useMemo(() => {
    if (!query) return allItems;
    const lowerQuery = query.toLowerCase();
    return allItems.filter(item =>
      item.name.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery)
    );
  }, [query, allItems]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (controlledIsOpen !== undefined) {
          // If controlled, toggle via the callback
          if (isOpen && onClose) onClose();
          else setInternalIsOpen(true); // This won't work for controlled, but Cmd+K should open
        } else {
          setInternalIsOpen(prev => !prev);
        }
      }
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, controlledIsOpen, onClose]);

  const handleSelect = useCallback((item: CommandItem | null) => {
    if (!item) return;
    setIsOpen(false);
    setQuery('');
    if (item.action) {
      item.action();
    } else if (item.href) {
      navigate(item.href);
    }
  }, [navigate]);

  const groupedItems = useMemo(() => {
    const groups: Record<string, CommandItem[]> = { navigation: [], action: [], recent: [] };
    filteredItems.forEach(item => {
      groups[item.category].push(item);
    });
    return groups;
  }, [filteredItems]);

  return (
    <>
      {/* Search trigger button - only show if showTrigger is true */}
      {showTrigger && (
        <button
          onClick={() => setIsOpen(true)}
          className={clsx(
            'flex items-center gap-2 px-3 py-2 rounded-lg',
            'bg-neutral-100 hover:bg-neutral-200',
            'text-neutral-500 text-sm',
            'transition-all duration-200',
            'border border-neutral-200 hover:border-neutral-300',
            'focus:outline-none focus:ring-2 focus:ring-primary-500'
          )}
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
          <span className="hidden sm:inline">Search...</span>
          <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium text-neutral-400 bg-neutral-200 rounded">
            <span className="text-xs">⌘</span>K
          </kbd>
        </button>
      )}

      {/* Command Palette Modal */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog onClose={() => setIsOpen(false)} className="relative z-50">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-neutral-900/50 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto p-4 sm:p-6 md:p-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="mx-auto max-w-2xl transform overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-neutral-200 transition-all">
                <Combobox onChange={handleSelect}>
                  {/* Search Input */}
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-neutral-400" />
                    <Combobox.Input
                      className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-neutral-900 placeholder:text-neutral-400 focus:ring-0 sm:text-sm"
                      placeholder="Search for pages, actions, or type a command..."
                      onChange={(e) => setQuery(e.target.value)}
                      autoComplete="off"
                    />
                  </div>

                  {/* Results */}
                  {filteredItems.length > 0 && (
                    <Combobox.Options static className="max-h-96 scroll-py-3 overflow-y-auto p-3">
                      {/* Navigation Section */}
                      {groupedItems.navigation.length > 0 && (
                        <div className="mb-4">
                          <h3 className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                            Navigation
                          </h3>
                          {groupedItems.navigation.map((item) => (
                            <CommandOption key={item.id} item={item} />
                          ))}
                        </div>
                      )}

                      {/* Actions Section */}
                      {groupedItems.action.length > 0 && (
                        <div>
                          <h3 className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
                            Quick Actions
                          </h3>
                          {groupedItems.action.map((item) => (
                            <CommandOption key={item.id} item={item} />
                          ))}
                        </div>
                      )}
                    </Combobox.Options>
                  )}

                  {/* Empty state */}
                  {query && filteredItems.length === 0 && (
                    <div className="px-6 py-14 text-center sm:px-14">
                      <MagnifyingGlassIcon className="mx-auto h-6 w-6 text-neutral-400" />
                      <p className="mt-4 text-sm text-neutral-900">
                        No results found for "<span className="font-semibold">{query}</span>"
                      </p>
                      <p className="mt-2 text-sm text-neutral-500">
                        Try searching for pages, actions, or clients.
                      </p>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between border-t border-neutral-100 bg-neutral-50 px-4 py-2.5">
                    <div className="flex items-center gap-4 text-xs text-neutral-500">
                      <span className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded text-neutral-600">↵</kbd>
                        to select
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded text-neutral-600">↑↓</kbd>
                        to navigate
                      </span>
                      <span className="flex items-center gap-1">
                        <kbd className="px-1.5 py-0.5 bg-neutral-200 rounded text-neutral-600">esc</kbd>
                        to close
                      </span>
                    </div>
                  </div>
                </Combobox>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

// Individual command option component
const CommandOption: React.FC<{ item: CommandItem }> = ({ item }) => {
  const Icon = item.icon;
  
  return (
    <Combobox.Option
      value={item}
      className={({ active }) =>
        clsx(
          'flex items-center gap-3 rounded-lg px-3 py-2.5 cursor-pointer transition-colors',
          active ? 'bg-primary-50 text-primary-900' : 'text-neutral-700'
        )
      }
    >
      {({ active }) => (
        <>
          <div className={clsx(
            'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
            active ? 'bg-primary-100' : 'bg-neutral-100'
          )}>
            <Icon className={clsx('h-5 w-5', active ? 'text-primary-600' : 'text-neutral-500')} />
          </div>
          <div className="flex-1 min-w-0">
            <p className={clsx('text-sm font-medium truncate', active ? 'text-primary-900' : 'text-neutral-900')}>
              {item.name}
            </p>
            {item.description && (
              <p className={clsx('text-xs truncate', active ? 'text-primary-700' : 'text-neutral-500')}>
                {item.description}
              </p>
            )}
          </div>
          {item.shortcut && (
            <kbd className={clsx(
              'hidden sm:inline-flex items-center px-2 py-1 text-xs font-medium rounded',
              active ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-500'
            )}>
              {item.shortcut}
            </kbd>
          )}
          <ArrowRightIcon className={clsx('h-4 w-4 flex-shrink-0', active ? 'text-primary-500' : 'text-neutral-300')} />
        </>
      )}
    </Combobox.Option>
  );
};

export default CommandPalette;
